import type { PlayInfo } from '@module/apis/client-side/live/type';
import SettingConfig from '@module/config/setting';
import parseSocketPayload from '@module/helpers/chat/parseSocketPayload';
import { aspCond } from '@module/helpers/conditions/condition';
import useSocketStore from '@module/stores/useSocketStore';
import type { CentrifugeMessage, ParsedPersonalMessage, ParsedPublicMessage } from '@module/types/centrifuge/messageTypes';
import { isPersonalRefreshTokenMessage, isPersonalSocketMessage } from '@module/types/centrifuge/personalMessageTypes';
import type { ChatServerDisconnectMessage } from '@module/types/centrifuge/public-message/etcMessageTypes';
import type Centrifuge from 'centrifuge';
import type { Subscription } from 'centrifuge';
import { useEffect, useRef } from 'react';
import useChatMessageStore from '../stores/useChatMessageStore';
import { useFetchLivePlayMutationStore } from '../stores/useFetchLivePlayMutationStore';
import useMuteStore from '../stores/useMuteStore';
import getPayloadFromToken, { createCentrifugeRefreshHandler, disconnectCentrifuge, initCentrifuge } from './centrifuge-features';
import TimeConstants from './item';
const MAX_RECONNECT_ATTEMPTS = 3;
const getBJname = () => {
  return window.location.pathname.split('/')[aspCond.if('iframe-asp') ? 3 : 2];
};
const isCurrentPagePlayPage = () => {
  const pathnames = window.location.pathname.split('/');
  return pathnames.includes('play');
};
const playInfoMatchesCurrPage = (playInfoUserId: PlayInfo['media']['userId']) => {
  const pathnames = window.location.pathname.split('/');
  return pathnames.includes(playInfoUserId);
};
interface Props {
  onDisconnect?: (reason: string) => void;
  onPublicMessage: (payload: ParsedPublicMessage) => void;
  onPersonalMessage: (payload: ParsedPersonalMessage) => void;

  /** * 최대 재연결 시도 횟수에 도달했을 때 호출되는 콜백 함수 */
  onMaxReconnectAttemptsReached?: () => void;
  chatServerDisconnectAlertMessage?: string;
}
const useCentrifuge = ({
  onPublicMessage: onMessage,
  onDisconnect,
  onPersonalMessage,
  onMaxReconnectAttemptsReached,
  chatServerDisconnectAlertMessage = '채팅 서버 연결이 끊어졌습니다.'
}: Props) => {
  const setChatMessages = useChatMessageStore(state => state.setChatMessages);
  const setSponMessages = useChatMessageStore(state_0 => state_0.setSponMessages);
  const setMuteTime = useMuteStore(state_1 => state_1.setMuteTime);
  const centrifugeState = useRef<Centrifuge | null>(null);
  const subscriptionState = useRef<Subscription | null>(null);
  const onMessageRef = useRef(onMessage);
  const onPersonalMessageRef = useRef(onPersonalMessage);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(undefined);
  const reconnectAttemptsRef = useRef<number>(0);
  const lastDisconnectReasonRef = useRef<string | null>(null);
  const {
    data: playInfo
  } = useFetchLivePlayMutationStore();
  const chatToken = playInfo?.token;
  useEffect(() => {
    return () => {
      setChatMessages([]);
      setSponMessages([]);
    };
  }, []);

  // callback 변경 사항을 반영하기 위함.
  onMessageRef.current = onMessage;
  onPersonalMessageRef.current = onPersonalMessage;
  const handleMessage = (payload: any) => {
    const parsedMessage = parseSocketPayload(payload);
    if (isPersonalSocketMessage(parsedMessage)) return;
    onMessageRef.current(parsedMessage);
  };
  const handlePersonalMessage = (payload_0: any) => {
    const parsedMessage_0 = parseSocketPayload(payload_0);
    if (!isPersonalSocketMessage(parsedMessage_0)) return;
    if (isPersonalRefreshTokenMessage(parsedMessage_0)) {
      // personal refresh token.
      (centrifugeState.current as any)?._refresh();
      return;
    }
    onPersonalMessageRef.current(parsedMessage_0);
  };
  const disconnectSocket = () => {
    if (centrifugeState.current) {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      disconnectCentrifuge(centrifugeState.current, subscriptionState.current);
      centrifugeState.current = null;
      subscriptionState.current = null;
      reconnectAttemptsRef.current = 0;
      lastDisconnectReasonRef.current = null;
    }
  };

  /** 재연결 시도중 메세지  */
  const addChatServerDisconnectMessage = () => {
    const msg: ChatServerDisconnectMessage = {
      data: {
        type: 'ChatServerDisconnect',
        message: chatServerDisconnectAlertMessage
      },
      offset: Date.now()
    };
    setChatMessages(prev => [...prev, msg]);
  };
  const handleDisconnect = async (ctx: {
    reason: string;
    reconnect: boolean;
  }) => {
    lastDisconnectReasonRef.current = ctx.reason;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (ctx.reason === 'already watch') {
      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
        await new Promise(res => {
          setTimeout(res, TimeConstants.ONE_SECOND * 9);
        });
        const reconnectAfterDisconnect = () => {
          const bjname = getBJname();
          if (!bjname || !playInfo) {
            return;
          }
          if (bjname !== playInfo.media.userId) {
            return;
          }
          const currentChatToken = useSocketStore.getState().token;
          if (currentChatToken) {
            connect(currentChatToken);
          }
          reconnectTimerRef.current = null;
        };
        reconnectAfterDisconnect();
        addChatServerDisconnectMessage();
        onDisconnect?.(ctx.reason);
      } else {
        console.error(`Max reconnect attempts reached: ${MAX_RECONNECT_ATTEMPTS}. Disconnecting socket.`);
        onMaxReconnectAttemptsReached?.();
      }
    } else if (ctx.reason === 'invalid token') {
      // 다른 이유로 연결이 끊어진 경우  ex) invalid token 등
      // 토큰이 유효하지 않은 경우, 로그아웃 처리 등 추가 작업 필요
    }
  };
  const handleConnect = async () => {
    const reconnectingForAlreadyWatch = lastDisconnectReasonRef.current === 'already watch';
    // "already watch"로 재연결 시 reconnectAttempts를 초기화하지 않음
    if (!reconnectingForAlreadyWatch) {
      reconnectAttemptsRef.current = 0;
    } else {
      await new Promise(res_0 => {
        setTimeout(res_0, TimeConstants.ONE_SECOND * 10);
      });
      if (centrifugeState.current?.isConnected()) {
        reconnectAttemptsRef.current = 0;
      }
    }
  };
  const setupEventListeners = (centrifugeInstance: Centrifuge) => {
    centrifugeInstance.removeAllListeners();
    subscriptionState.current?.removeAllListeners();
    subscriptionState.current?.unsubscribe();
    subscriptionState.current = null;
    centrifugeInstance.on('disconnect', handleDisconnect);
    centrifugeInstance.on('publish', handlePersonalMessage);
    centrifugeInstance.on('connect', handleConnect);
    const {
      channel
    } = useSocketStore.getState();
    if (channel) {
      subscriptionState.current = centrifugeInstance.subscribe(channel, handleMessage);
    }
  };
  const connect = (token: string) => {
    useSocketStore.getState().setToken(token);
    useSocketStore.getState().setChannel(getPayloadFromToken(token).info.channel);
    if (!centrifugeState.current) {
      const refreshHandler = createCentrifugeRefreshHandler({
        getAuthContext: () => {
          const {
            token: token_0,
            channel: channel_0
          } = useSocketStore.getState();
          return {
            token: token_0,
            channel: channel_0
          };
        },
        onTokenRefreshed: nextToken => {
          useSocketStore.getState().setToken(nextToken);
        }
      });
      centrifugeState.current = initCentrifuge({
        url: SettingConfig.PANDA_CHAT_SOCKET,
        refresh: {
          handler: refreshHandler
        }
      });
    }
    setupEventListeners(centrifugeState.current);
    centrifugeState.current.setToken(token);
    centrifugeState.current.connect();
  };
  const chatTokenEffectCleanup = () => {
    if (centrifugeState.current) {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      disconnectCentrifuge(centrifugeState.current, subscriptionState.current);
      centrifugeState.current.removeAllListeners();
      subscriptionState.current?.removeAllListeners();
      subscriptionState.current?.unsubscribe();
      centrifugeState.current = null;
      subscriptionState.current = null;
      reconnectAttemptsRef.current = 0;
      lastDisconnectReasonRef.current = null;
    }
    setMuteTime(0);
  };
  useEffect(() => {
    if (!chatToken || playInfo?.chatMode === 'disable' || isCurrentPagePlayPage() && !playInfoMatchesCurrPage(playInfo.media.userId)) {
      return chatTokenEffectCleanup;
    }
    connect(chatToken);
    return chatTokenEffectCleanup;
  }, [chatToken]);

  /**
   * 테스트용: mock 메시지를 주입하여 handleMessage를 트리거합니다.
   * 개발 환경에서만 사용하세요.
   */
  const triggerMockMessage = (mockPayload: CentrifugeMessage) => {
    handleMessage(mockPayload);
  };

  /**
   * 테스트용: mock 개인 메시지를 주입하여 handlePersonalMessage를 트리거합니다.
   * 개발 환경에서만 사용하세요.
   */
  const triggerMockPersonalMessage = (mockPayload_0: CentrifugeMessage) => {
    handlePersonalMessage(mockPayload_0);
  };
  return {
    disconnectSocket,
    triggerMockMessage,
    triggerMockPersonalMessage
  };
};
export default useCentrifuge;